<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useAnalytics } from '@/shared/analytics'
import { useBradleyEnvironment } from '@/shared/composables/use-bradley-environment.js'
import { BRADLEY_ANALYTICS_EVENTS } from '../analytics/bradley-analytics.constants.js'
import { useBradleyComposerAttachments } from '../composables/use-bradley-composer-attachments.js'
import { useBradleyComposerRecorder } from '../composables/use-bradley-composer-recorder.js'
import { areAllBradleyAttachmentsReadyForSend, isBradleyAttachmentReadyForSend } from '../utils/bradley-attachment.util.js'
import BradleyComposerRecorderPreview from './bradley-composer-recorder-preview.vue'
import BradleyPlusMenu from './bradley-plus-menu.vue'
import BradleyStagedAttachments from './bradley-staged-attachments.vue'
import BradleyUploadErrorBanner from './bradley-upload-error-banner.vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  resolveConversationId: {
    type: Function,
    default: null,
  },
  selectedTools: {
    type: Array,
    default: () => [],
  },
  uploading: {
    type: Boolean,
    default: false,
  },
  uploadedDocuments: {
    type: Array,
    default: () => [],
  },
  selectedUploadedDocuments: {
    type: Array,
    default: () => [],
  },
  projectId: {
    type: String,
    default: null,
  },
  conversationId: {
    type: [String, Number],
    default: null,
  },
  projectFiles: {
    type: Array,
    default: () => [],
  },
  projectTitle: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['send', 'update:modelValue', 'removeTool', 'selectTool', 'fileUploaded', 'update:selectedUploadedDocuments', 'showToast', 'reuseProjectFiles'])

const { trackEvent } = useAnalytics()
const userMessage = ref(props.modelValue)
const plusMenuReference = ref(null)
const textareaReference = ref(null)
const typePromptTracked = ref(false)
const { documentUploadEnabled: DOCUMENT_UPLOAD_ENABLED, newFileUploadEnabled: NEW_FILE_UPLOAD_ENABLED } = useBradleyEnvironment()

const selectedDocumentsModel = computed({
  get: () => props.selectedUploadedDocuments,
  set: value => emit('update:selectedUploadedDocuments', value),
})

const canSendMessage = computed(() => {
  if (!areAllBradleyAttachmentsReadyForSend(selectedDocumentsModel.value)) {
    return false
  }

  const hasReadyAttachments = selectedDocumentsModel.value.some(isBradleyAttachmentReadyForSend)
  return Boolean(userMessage.value.trim()) || hasReadyAttachments
})

const {
  acceptAttribute,
  dismissUploadError,
  fileInput,
  handleFileSelected,
  handlePaste,
  isDragging,
  onComposerDragLeave,
  onComposerDragOver,
  onComposerDrop,
  openFilePickerFromMenu,
  removeSelectedUploadedDocument,
  uploadErrors,
} = useBradleyComposerAttachments({
  documentUploadEnabled: DOCUMENT_UPLOAD_ENABLED,
  newFileUploadEnabled: NEW_FILE_UPLOAD_ENABLED,
  selectedDocumentsModel,
  emit,
  surface: 'home_composer',
})

const {
  isInitializing: isInitializingRecorder,
  isRecording,
  isPaused,
  isTranscribing,
  formattedDuration,
  waveformData,
  hasPreview: hasRecorderPreview,
  pauseRecording,
  resumeRecording,
  toggleRecording,
  discardRecording,
} = useBradleyComposerRecorder({
  emit,
  resolveConversationId: seedTitle => (
    typeof props.resolveConversationId === 'function'
      ? props.resolveConversationId(seedTitle)
      : props.conversationId
  ),
  onTranscriptReady: handleTranscriptReady,
})

function handleTranscriptReady(text) {
  const nextText = String(text ?? '')
  const current = String(userMessage.value ?? '').trimEnd()
  userMessage.value = current ? `${current} ${nextText}` : nextText
  nextTick(() => {
    autoResizeTextarea()
    textareaReference.value?.focus?.()
  })
}

function handleComposerInput() {
  autoResizeTextarea()
  if (!typePromptTracked.value && userMessage.value.trim().length > 0) {
    typePromptTracked.value = true
    trackEvent(BRADLEY_ANALYTICS_EVENTS.TYPE_PROMPT, { surface: 'home_composer' })
  }
}

function autoResizeTextarea() {
  if (textareaReference.value) {
    textareaReference.value.style.height = 'auto'
    const newHeight = textareaReference.value.scrollHeight
    const maxHeight = 200

    if (newHeight >= maxHeight) {
      textareaReference.value.style.height = `${maxHeight}px`
      textareaReference.value.style.overflowY = 'auto'
    }
    else {
      textareaReference.value.style.height = `${newHeight}px`
      textareaReference.value.style.overflowY = 'hidden'
    }
  }
}

watch(() => props.modelValue, (newValue) => {
  userMessage.value = newValue
  nextTick(() => {
    autoResizeTextarea()
  })
})

onMounted(() => {
  if (String(userMessage.value ?? '').trim()) {
    nextTick(() => {
      autoResizeTextarea()
    })
  }
})

watch(userMessage, (newValue) => {
  emit('update:modelValue', newValue)
  if (!String(newValue ?? '').trim()) {
    typePromptTracked.value = false
  }
})

function handleSend() {
  const trimmed = userMessage.value.trim()
  if (!trimmed && selectedDocumentsModel.value.length === 0) {
    trackEvent(BRADLEY_ANALYTICS_EVENTS.SEND_PROMPT_BLOCKED_NO_TEXT, {
      has_files_attached: selectedDocumentsModel.value.length > 0,
      file_count: selectedDocumentsModel.value.length,
      block_reason: 'empty_prompt',
    })
    return
  }

  if (props.uploading || isRecording.value || isPaused.value || isInitializingRecorder.value || isTranscribing.value) {
    return
  }

  emit('send', trimmed)

  userMessage.value = ''
  discardRecording()

  if (textareaReference.value) {
    textareaReference.value.style.height = 'auto'
  }
}

function handleChatKeydown(event) {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    if (!canSendMessage.value) {
      trackEvent(BRADLEY_ANALYTICS_EVENTS.SEND_PROMPT_BLOCKED_NO_TEXT, {
        has_files_attached: selectedDocumentsModel.value.length > 0,
        file_count: selectedDocumentsModel.value.length,
        block_reason: 'empty_prompt',
      })
      return
    }
    if (props.uploading) {
      return
    }
    handleSend()
  }
}

function handleInputFocus() {
  if (plusMenuReference.value?.closeMenu) {
    plusMenuReference.value.closeMenu()
  }
}

function removeTool(toolLabel) {
  emit('removeTool', toolLabel)
}

function onSelectToolFromPlus(toolLabel) {
  emit('selectTool', toolLabel)
}
</script>

<template>
  <div class="input-area-container">
    <div
      class="relative w-full border border-dss-surface-200 rounded-2xl bg-white pl-4 pt-3 pb-3 pr-[15px] transition-colors"
      :class="{ 'ring-2 ring-indigo-400 ring-offset-1 bg-indigo-50/40': DOCUMENT_UPLOAD_ENABLED && isDragging }"
      @dragover.prevent="onComposerDragOver"
      @dragleave.prevent="onComposerDragLeave"
      @drop.prevent="onComposerDrop"
    >
      <input
        v-if="DOCUMENT_UPLOAD_ENABLED"
        ref="fileInput"
        type="file"
        multiple
        :accept="acceptAttribute"
        class="hidden"
        @change="handleFileSelected"
      >

      <bradley-staged-attachments
        :items="selectedUploadedDocuments"
        @remove="removeSelectedUploadedDocument"
      />

      <bradley-composer-recorder-preview
        v-if="hasRecorderPreview"
        :is-recording="isRecording"
        :is-paused="isPaused"
        :is-transcribing="isTranscribing"
        :duration-label="formattedDuration"
        :waveform-data="waveformData"
        @pause="pauseRecording"
        @resume="resumeRecording"
        @discard="discardRecording"
      />

      <bradley-upload-error-banner
        :errors="uploadErrors"
        @dismiss="dismissUploadError"
      />

      <textarea
        v-show="!isRecording && !isPaused && !isTranscribing"
        ref="textareaReference"
        v-model="userMessage"
        rows="1"
        :placeholder="$t('newBradley.input.placeholder')"
        class="w-full max-h-[200px] overflow-x-hidden px-4 py-3 pb-3 border border-dss-surface-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-dss-primary-500 focus:border-transparent bg-white font-inter text-base font-normal leading-[163%] tracking-normal text-slate-700 placeholder:font-inter placeholder:font-normal placeholder:text-base placeholder:leading-[163%] placeholder:tracking-normal placeholder:text-dss-surface-700"
        @keydown="handleChatKeydown"
        @focus="handleInputFocus"
        @input="handleComposerInput"
        @paste="handlePaste"
      />

      <div class="flex items-center justify-between gap-4 mt-2">
        <div class="flex items-center gap-2 flex-wrap min-w-0">
          <bradley-plus-menu
            v-if="!isRecording && !isPaused && !isTranscribing"
            ref="plusMenuReference"
            :project-id="projectId"
            :project-title="projectTitle"
            :conversation-id="conversationId"
            :project-files="projectFiles"
            @add-file="openFilePickerFromMenu"
            @select-tool="onSelectToolFromPlus"
            @reuse-project-files="files => emit('reuseProjectFiles', files)"
          />

          <div
            v-for="tool in selectedTools"
            :key="tool"
            class="inline-flex items-center gap-2 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm"
          >
            <button
              class="hover:bg-gray-300 rounded p-0.5 transition-colors"
              type="button"
              @click="removeTool(tool)"
            >
              <i class="pi pi-times text-xs text-gray-600" />
            </button>
            <span class="font-medium">{{ tool }}</span>
          </div>
        </div>

        <div class="flex items-center gap-2 shrink-0">
          <button
            type="button"
            class="bradley-input-area__voice inline-flex size-8 items-center justify-center rounded-lg text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed dark:text-dss-surface-600 dark:bg-dss-surface-200 dark:hover:bg-dss-surface-300"
            :class="{ 'bradley-input-area__voice--recording text-white bg-red-600 hover:bg-red-700 dark:text-white dark:bg-red-600 dark:hover:bg-red-700': isRecording || isPaused }"
            :disabled="uploading || isInitializingRecorder || isTranscribing"
            :title="(isRecording || isPaused) ? $t('newBradley.input.stopRecording') : $t('newBradley.input.recordAudio')"
            :aria-label="(isRecording || isPaused) ? $t('newBradley.input.stopRecording') : $t('newBradley.input.recordAudio')"
            @click="toggleRecording"
          >
            <i
              :class="(isRecording || isPaused) ? 'pi pi-stop text-sm' : 'pi pi-microphone text-sm'"
            />
          </button>
          <button
            type="button"
            class="bradley-input-area__send inline-flex size-8 items-center justify-center rounded-lg bg-[#0000CC] text-white hover:bg-[#0000B3] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#0000CC]"
            :disabled="!canSendMessage || uploading || isRecording || isPaused || isInitializingRecorder || isTranscribing"
            :title="$t('newBradley.input.sendMessage')"
            @click="handleSend"
          >
            <i class="pi pi-arrow-up text-sm" />
          </button>
        </div>
      </div>
    </div>

    <p
      v-if="DOCUMENT_UPLOAD_ENABLED"
      class="bradley-input-area__limits"
    >
      <i
        class="pi pi-info-circle bradley-input-area__limits-icon"
        aria-hidden="true"
      />
      <span class="bradley-input-area__limits-text">{{
        $t(NEW_FILE_UPLOAD_ENABLED
          ? 'newBradley.input.uploadLimitsDisclaimer'
          : 'newBradley.input.uploadLimitsDisclaimerLegacy')
      }}</span>
    </p>
  </div>
</template>

<style scoped>
.input-area-container {
  margin: 3rem 0 0;
}

.bradley-input-area__limits {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  margin: 0.75rem 0 0;
  text-align: left;
}

.bradley-input-area__limits-icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 0.875rem;
  height: 0.875rem;
  font-size: 0.875rem;
  line-height: 1;
  color: #a3a3c4;
}

.bradley-input-area__limits-icon::before {
  line-height: 1;
}

.bradley-input-area__limits-text {
  font-family: var(--font-inter, 'Inter', sans-serif);
  font-size: 12px;
  font-weight: 400;
  font-style: normal;
  line-height: 163%;
  letter-spacing: 0;
  color: #7a7a93;
}
</style>
